export {
  PAYPAL_SUPPORTED_COUNTRIES,
  US_STATES,
  CA_PROVINCES,
  getCountryNameByCode,
  getCountryCodeByName,
  isPayPalSupportedCountry,
  getStatesForCountry,
  getCountryName,
  getStateName,
  getStateCodeByName,
  getStateOrRegionName,
  formatStateForPayPal,
  transformPayPalCountriesToDropdownOptions,
  getPayPalCountriesWithIsoFormat,
  transformStatesToDropdownOptions,
  isoToPayPalCode,
  payPalToIsoCode,
  isCountrySupportedByPayPal,
  isCountryBannedForShipping,
  type PayPalCountry,
  type PayPalCountryWithIso,
} from "@/lib/paypal-countries";

import { getCountries, getCountryCallingCode } from "libphonenumber-js";

export interface PhoneCountry {
  isoCode: string;
  code: string;
  name: string;
  flag: string;
}

function countryFlagUrl(isoCode: string): string {
  if (isoCode === "AC") {
    return "https://upload.wikimedia.org/wikipedia/commons/6/65/Flag_of_Ascension_Island.svg";
  }
  if (isoCode === "TA") {
    return "https://upload.wikimedia.org/wikipedia/commons/8/89/Flag_of_Tristan_da_Cunha.svg";
  }
  return `https://flagcdn.com/w40/${isoCode.toLowerCase()}.png`;
}

let phoneCountriesCache: PhoneCountry[] | null = null;

export function getPhoneCountries(): PhoneCountry[] {
  if (phoneCountriesCache) return phoneCountriesCache;

  phoneCountriesCache = getCountries()
    .map((isoCode) => ({
      isoCode,
      code: `+${getCountryCallingCode(isoCode)}`,
      name: new Intl.DisplayNames(["en"], { type: "region" }).of(isoCode) || "",
      flag: countryFlagUrl(isoCode),
    }))
    .filter((country) => country.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  return phoneCountriesCache;
}

export function getPhoneCountryByIso(isoCode?: string | null): PhoneCountry | null {
  if (!isoCode) return null;
  return getPhoneCountries().find((c) => c.isoCode === isoCode) ?? null;
}

export function getPhoneCountryByDial(dial?: string | null): PhoneCountry | null {
  if (!dial) return null;
  const matches = getPhoneCountries().filter((c) => c.code === dial);
  if (matches.length === 0) return null;
  if (dial === "+1") return matches.find((c) => c.isoCode === "US") ?? matches[0];
  return matches[0];
}

export function fuzzyMatchPhoneCountry(query: string, country: PhoneCountry): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    country.name.toLowerCase().includes(q) ||
    country.isoCode.toLowerCase().includes(q) ||
    country.code.includes(q.startsWith("+") ? q : `+${q}`)
  );
}

export const DEFAULT_PHONE_COUNTRY: PhoneCountry =
  getPhoneCountryByIso("US") ??
  getPhoneCountries()[0] ?? {
    isoCode: "US",
    code: "+1",
    name: "United States",
    flag: countryFlagUrl("US"),
  };
